/**
 * Voice Interview Interface — Split-Screen Layout
 *
 * Architecture:
 *  - Audio:       Browser ↔ OpenAI via WebRTC (RTCPeerConnection)
 *  - Tool events: Browser ↔ Backend via WebSocket (/ws/{session_id})
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import APIService from '../services/APIService';
import { getIdToken } from '../lib/firebase';
import { friendlyVoiceError } from './voiceError';
import InterviewAvatar from './nano/InterviewAvatar';
import { PERSONAS, personaOf, roleOf } from './nano/interviewData';
import { IconChat, IconGear, IconPerson, IconX } from './nano/icons';
import '../styles/prepare-up-call.css';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BACKEND_WS_URL = BACKEND_URL.replace(/^http/, 'ws');

// Animated waveform — active bars animate, inactive are static dots
const WaveBar = ({ active, color, count = 12 }) => (
  <div className="vi-sp-wave">
    {Array.from({ length: count }).map((_, i) =>
      active ? (
        <div
          key={i}
          className="vi-sp-bar"
          style={{ background: color, animationDelay: `${i * 0.08}s` }}
        />
      ) : (
        <div key={i} className="vi-sp-bar-still" style={{ background: color }} />
      )
    )}
  </div>
);


// Crisp stroke icons for the control bar (system emoji render inconsistently)
const IcMic = ({ off, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="18" x2="12" y2="22" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);
const IcCam = ({ off, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="6" width="13" height="12" rx="2.5" />
    <path d="M15 10.5 21 7v10l-6-3.5" />
    {off && <line x1="2" y1="2" x2="22" y2="22" />}
  </svg>
);
const IcBulb = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18h6M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.4 1 2.3h6c0-.9.4-1.8 1-2.3A7 7 0 0 0 12 2Z" />
  </svg>
);

const formatTime = (s) =>
  `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

// ─────────────────────────────────────────────────────────────────────────────

const VoiceInterviewInterface = ({ sessionData, onComplete, onCancel, setup }) => {
  // sessionData is null while NanoApp is still building the question pack.
  const sid = sessionData?.session_id;
  // Core status
  const [status, setStatus]               = useState('initializing');
  const [error, setError]                 = useState(null);
  const [interviewStarted, setInterviewStarted] = useState(false);
  // Sparky's prelude covers the stage until Alex actually speaks: connecting →
  // ready (Begin ignites) → joining (Begin pressed, first response pending).
  const [aiJoined, setAiJoined]           = useState(false);
  // Background-noise watch — a small honest meter over the mic stream.
  const [noisy, setNoisy]                 = useState(false);
  const startedRef = useRef(false);       // mirrors interviewStarted for closures
  const audioCtxRef = useRef(null);
  const noiseTimerRef = useRef(null);
  const stopNoiseWatch = useCallback(() => {
    clearInterval(noiseTimerRef.current);
    try { audioCtxRef.current?.close?.(); } catch { /* already closed */ }
    audioCtxRef.current = null;
    setNoisy(false);
  }, []);
  const startNoiseWatch = useCallback((stream) => {
    stopNoiseWatch();
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.fftSize);
      const recent = [];
      noiseTimerRef.current = setInterval(() => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let k = 0; k < buf.length; k += 1) { const d = (buf[k] - 128) / 128; sum += d * d; }
        recent.push(Math.sqrt(sum / buf.length));
        if (recent.length > 12) recent.shift();
        if (recent.length >= 8) {
          // the FLOOR (~25th percentile) separates a constant hum/chatter from
          // normal speech bursts — speech dips between words, noise doesn't
          const floor = [...recent].sort((a, b) => a - b)[2];
          setNoisy(floor > 0.045);
        }
      }, 400);
    } catch { /* detection is best-effort, never blocks the room */ }
  }, [stopNoiseWatch]);

  // Content
  const [messages, setMessages]           = useState([]);
  const [aiPartial, setAiPartial]         = useState('');
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  // The candidate's words: streamed deltas when the session's transcription model
  // sends them, otherwise the finished sentence once speech stops.
  const [userPartial, setUserPartial] = useState('');
  const [userPending, setUserPending] = useState(false);   // speech ended, transcript not back yet

  // Phase / question tracking — driven by backend stage_changed events OR text detection fallback
  const [currentStage, setCurrentStage]   = useState(0); // 0=Intro, 1..N=question, N+1=concluded
  const [totalQ, setTotalQ]               = useState(sessionData?.total_questions || 3);
  const [questionsData, setQuestionsData] = useState([]); // [{id, question, hints, hint_count}]

  // Hints — revealed locally from questionsData.hints array
  const [hintsByQ, setHintsByQ]           = useState({}); // { qIdx: [text, ...] }
  const hintsByQRef = useRef({});

  // Timer
  const [elapsed, setElapsed]             = useState(0);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Refs
  const pcRef         = useRef(null);
  const dcRef         = useRef(null);
  const wsBackendRef  = useRef(null);
  const audioElRef    = useRef(null);
  const streamRef     = useRef(null);
  const messagesEndRef = useRef(null);

  // Live webcam preview for the interview room (video is local-only — never
  // uploaded; audio goes through the existing realtime connection).
  const videoRef = useRef(null);
  const camStreamRef = useRef(null);
  const [camError, setCamError] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  // A voice interview starts with the camera off; video (and legacy launches) on.
  const [camOff, setCamOff] = useState(setup?.type === 'voice');
  const camOffRef = useRef(setup?.type === 'voice');
  const camAliveRef = useRef(true);
  const acquireCam = useCallback(() => {
    if (camStreamRef.current || !navigator.mediaDevices?.getUserMedia) { if (!navigator.mediaDevices?.getUserMedia) setCamError(true); return; }
    navigator.mediaDevices.getUserMedia({ video: { width: 960 }, audio: false })
      .then((stream) => {
        if (!camAliveRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
        camStreamRef.current = stream;
        stream.getVideoTracks().forEach((t) => { t.enabled = !camOffRef.current; });
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamError(false);
      })
      .catch(() => { if (camAliveRef.current) setCamError(true); });
  }, []);

  const toggleMic = useCallback(() => {
    setMicMuted((m) => {
      streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = m; });
      return !m;
    });
  }, []);
  const toggleCam = useCallback(() => {
    const next = !camOffRef.current;
    camOffRef.current = next;
    setCamOff(next);
    camStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = !next; });
    if (!next && !camStreamRef.current) acquireCam();
  }, [acquireCam]);
  useEffect(() => {
    camAliveRef.current = true;
    if (!camOffRef.current) acquireCam();
    return () => {
      camAliveRef.current = false;
      camStreamRef.current?.getTracks().forEach((t) => t.stop());
      camStreamRef.current = null;
    };
  }, [acquireCam]);
  useEffect(() => {
    // The video element only exists once the room leaves 'initializing' —
    // re-attach the already-acquired stream whenever it (re)mounts.
    if (videoRef.current && camStreamRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = camStreamRef.current;
    }
  });
  const aiBufferRef   = useRef('');
  const completedRef  = useRef(false);
  const startTimeRef  = useRef(Date.now());
  const messagesRef   = useRef([]);
  const statusRef     = useRef('initializing');
  const questionsDataRef = useRef([]); // mirror of questionsData for use in callbacks
  const currentStageRef  = useRef(0);  // mirror of currentStage for use in callbacks

  const setStatusBoth = useCallback((s) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  // Keep refs in sync with state so callbacks always see latest values
  const setCurrentStageBoth = useCallback((s) => {
    currentStageRef.current = s;
    setCurrentStage(s);
  }, []);

  // ── Forward raw OpenAI event to backend for session logging ──────────────
  const logOpenAiEvent = useCallback((eventType, payload) => {
    const ws = wsBackendRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'openai_event', event_type: eventType, payload }));
    }
  }, []);

  // ── Send to OpenAI datachannel + log outgoing event ──────────────────────
  const dcSend = useCallback((payload) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    dc.send(JSON.stringify(payload));
    const ws = wsBackendRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'openai_event',
        event_type: `browser→openai:${payload.type}`,
        payload,
      }));
    }
  }, []);

  const elapsedRef = useRef(0);   // seconds into the interview — stamps each transcript line
  const addMessage = useCallback((role, text) => {
    setMessages(prev => {
      const next = [...prev, { role, text, id: Date.now() + Math.random(), t: elapsedRef.current }];
      messagesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => { hintsByQRef.current = hintsByQ; }, [hintsByQ]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!interviewStarted || status === 'completed') return;
    const iv = setInterval(() => setElapsed((s) => { elapsedRef.current = s + 1; return s + 1; }), 1000);
    return () => clearInterval(iv);
  }, [interviewStarted, status]);

  // ── Auto-scroll chat ───────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiPartial]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const phases = useMemo(() => {
    const base = ['Intro'];
    for (let i = 1; i <= totalQ; i++) base.push(`Q ${i}`);
    return base;
  }, [totalQ]);

  const totalPhases = phases.length; // Intro + N questions
  const progress = Math.min((currentStage / totalPhases) * 100, 100);

  // Current question (0-indexed into questionsData)
  const currentQData = currentStage >= 1 ? questionsData[currentStage - 1] : null;
  const hintCount    = currentQData?.hints?.length ?? currentQData?.hint_count ?? 0;
  const revealedHintsForQ = hintsByQ[currentStage - 1] || [];
  const hintsUsed    = revealedHintsForQ.length;

  // ── Completion ─────────────────────────────────────────────────────────────
  const triggerCompletion = useCallback((early = false) => {
    if (completedRef.current) return;
    const endedEarly = early === true && currentStageRef.current < (questionsDataRef.current.length || 1);
    completedRef.current = true;
    setStatusBoth('completed');
    stopNoiseWatch();
    dcRef.current?.close();
    pcRef.current?.close();
    setTimeout(async () => {
      try {
        const duration = (Date.now() - startTimeRef.current) / 1000;
        // Exclude tool-call messages from transcript (only ai/user speech)
        const transcript = messagesRef.current
          .filter(m => m.role === 'ai' || m.role === 'user')
          .map(m => ({ role: m.role, text: m.text }));
        const hintsUsedByQ = Object.fromEntries(
          Object.entries(hintsByQRef.current || {}).map(([k, v]) => [Number(k) + 1, (v || []).length]),
        );
        const report = await APIService.getVoiceReport(sid, transcript, duration, hintsUsedByQ);
        // each question's hub topic/concept rides along so feedback can link to Practice
        const questionsMeta = questionsDataRef.current.map((q) => ({ topic: q.topic || '', concept: q.concept || '' }));
        onComplete({ ...report, questions_meta: questionsMeta, ended_early: endedEarly });
      } catch (err) {
        console.error('Report generation failed:', err);
        onComplete({ session_id: sid, voice_interview: true, error: err.message });
      }
    }, 2000);
  }, [setStatusBoth, sid, onComplete, stopNoiseWatch]);

  // Failsafe: if no response event lands within 4s of Begin, show the room
  // anyway — the prelude must never hold a working session hostage.
  useEffect(() => {
    if (!interviewStarted || aiJoined) return undefined;
    const t = setTimeout(() => setAiJoined(true), 4000);
    return () => clearTimeout(t);
  }, [interviewStarted, aiJoined]);

  // ── Start button ───────────────────────────────────────────────────────────
  const handleStart = useCallback(() => {
    if (dcRef.current?.readyState === 'open') {
      // Unmute the mic ONLY now — pre-Begin the room must stay deaf, or server
      // VAD hears background noise and Alex starts the interview by himself.
      startedRef.current = true;
      streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = true; });
      audioCtxRef.current?.resume?.().catch?.(() => {});
      dcSend({ type: 'response.create' });
      setInterviewStarted(true);
      setStatusBoth('speaking');
    }
  }, [dcSend, setStatusBoth]);

  // ── Stage detection fallback — match AI text against known questions ────────
  // Used when the LLM speaks a question but the tool event is delayed or missing.
  const detectStageFromAiText = useCallback((text) => {
    const lower = text.toLowerCase();
    const qs = questionsDataRef.current;

    // Match spoken text against question snippets
    for (let i = 0; i < qs.length; i++) {
      const snippet = (qs[i]?.question || '').toLowerCase().slice(0, 50);
      if (snippet.length >= 15 && lower.includes(snippet)) {
        const newStage = i + 1;
        if (currentStageRef.current < newStage) setCurrentStageBoth(newStage);
        return;
      }
    }

    // Detect "first question" intro keywords → advance to Q1
    if (currentStageRef.current === 0 && qs.length > 0) {
      const startsQ1 = [
        "first question", "question one", "question 1",
        "let's begin", "let's start", "shall we start", "ready to begin",
        "here's your first",
      ];
      if (startsQ1.some(k => lower.includes(k))) setCurrentStageBoth(1);
    }

    // Detect "next question" / "question N" advances
    const nextMatch = lower.match(/(?:next question|question\s+(\d+)|moving on to)/);
    if (nextMatch) {
      const stated = nextMatch[1] ? parseInt(nextMatch[1], 10) : currentStageRef.current + 1;
      if (stated > currentStageRef.current && stated <= qs.length) {
        setCurrentStageBoth(stated);
      }
    }
  }, [setCurrentStageBoth]);

  // ── Hint button — reveal locally from questionsData.hints array ────────────
  const handleHint = useCallback(() => {
    const stage = currentStageRef.current;
    if (stage < 1) return;
    const qIdx = stage - 1;
    const qs = questionsDataRef.current;
    const hints = qs[qIdx]?.hints || [];
    const alreadyRevealed = hintsByQ[qIdx]?.length || 0;

    if (alreadyRevealed < hints.length) {
      const hintText = hints[alreadyRevealed];
      setHintsByQ(prev => {
        const arr = [...(prev[qIdx] || [])];
        arr[alreadyRevealed] = hintText;
        return { ...prev, [qIdx]: arr };
      });
      // Tell the AI to read the hint aloud
      dcSend({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: 'Can I get a hint please?' }],
        },
      });
      dcSend({ type: 'response.create' });
    }
  }, [dcSend, hintsByQ]);

  // ── UI updates from backend tools ─────────────────────────────────────────
  const applyUiUpdate = useCallback((upd) => {
    if (!upd) return;
    switch (upd.action) {
      // Fired by get_interview_context() — AI has loaded its config
      case 'interview_context_loaded':
        if (upd.total_questions) setTotalQ(upd.total_questions);
        // Stay at stage 0 (Intro) — AI will greet then call get_question(1)
        break;

      // Fired by get_question(N) — a question is now being asked
      case 'stage_changed':
        setCurrentStageBoth(upd.stage);
        // hint_count per-question may be included in the event
        if (upd.hint_count != null && upd.stage >= 1) {
          // questionsData already has hints from token response — no action needed
        }
        break;

      case 'hint_revealed':
        if (upd.text && upd.question_index >= 0 && upd.hint_index >= 0) {
          setHintsByQ(prev => {
            const arr = [...(prev[upd.question_index] || [])];
            if (!arr[upd.hint_index]) arr[upd.hint_index] = upd.text;
            return { ...prev, [upd.question_index]: arr };
          });
        }
        break;

      case 'evaluation':
        // Score received from evaluate_response — could show a subtle indicator
        break;

      case 'interview_concluded':
        triggerCompletion();
        break;

      case 'section_change':
        if (upd.section === 'completed') triggerCompletion();
        break;

      case 'send':
        setNotifications(prev => [
          ...prev,
          { id: Date.now(), content: upd.content, msg_type: upd.msg_type || 'info' },
        ]);
        break;

      default:
        break;
    }
  }, [triggerCompletion, setCurrentStageBoth]);

  // ── Data channel handler (OpenAI → browser) ───────────────────────────────
  const handleDcEvent = useCallback((event) => {
    let msg;
    try { msg = JSON.parse(event.data); } catch { return; }

    // Forward every event to backend for session logging (non-blocking)
    logOpenAiEvent(msg.type, msg);

    switch (msg.type) {
      case 'session.created':
      case 'session.updated':
        break;

      case 'response.created':
        if (statusRef.current !== 'completed') setStatusBoth('speaking');
        setAiJoined(true);          // Alex is talking — the prelude's job is done
        break;

      case 'response.audio_transcript.delta':
        aiBufferRef.current += msg.delta;
        setAiPartial(aiBufferRef.current);
        if (statusRef.current !== 'completed') setStatusBoth('speaking');
        break;

      case 'response.audio_transcript.done': {
        const text = msg.transcript || aiBufferRef.current;
        if (text && text.trim()) addMessage('ai', text.trim());
        aiBufferRef.current = '';
        setAiPartial('');
        const lc = (text || '').toLowerCase();
        // Fallback completion detection
        if (lc.includes('interview is now complete') || lc.includes('that wraps up all our questions')) {
          triggerCompletion();
        } else {
          // Fallback stage detection — detect which question the AI just asked
          detectStageFromAiText(lc);
          if (statusRef.current !== 'completed') setStatusBoth('listening');
        }
        break;
      }

      case 'conversation.item.input_audio_transcription.delta':
        if (msg.delta) setUserPartial((p) => p + msg.delta);
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (msg.transcript?.trim()) addMessage('user', msg.transcript.trim());
        setUserPartial(''); setUserPending(false);
        break;

      case 'input_audio_buffer.speech_started':
        setIsUserSpeaking(true); setUserPending(false);
        if (statusRef.current !== 'completed') setStatusBoth('listening');
        break;

      case 'input_audio_buffer.speech_stopped':
        setIsUserSpeaking(false); setUserPending(true);
        if (statusRef.current !== 'completed') setStatusBoth('processing');
        break;

      case 'response.done':
        if (statusRef.current !== 'completed') setStatusBoth('listening');
        break;

      case 'response.function_call_arguments.done': {
        const { call_id, name, arguments: rawArgs } = msg;
        // Show tool call badge inline in chat
        let parsedArgs = {};
        try { parsedArgs = JSON.parse(rawArgs || '{}'); } catch { /* noop */ }
        addMessage('tool', { call_id, name, args: parsedArgs, status: 'calling' });
        const ws = wsBackendRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'tool_call', call_id, name, arguments: rawArgs }));
        }
        break;
      }

      case 'error':
        console.error('OpenAI DC error:', msg);
        setError(friendlyVoiceError(msg.error, 'The voice connection dropped'));
        setStatusBoth('error');
        break;

      default:
        break;
    }
  }, [addMessage, setStatusBoth, triggerCompletion, detectStageFromAiText, logOpenAiEvent]);

  // ── Init ──────────────────────────────────────────────────────────────────
  // Monotonic connection epoch — guards against React StrictMode's double-mount
  // (and rapid Retry clicks) spawning overlapping WebRTC connections. Each
  // connect() captures its own epoch; any in-flight connect whose epoch is no
  // longer current bails instead of creating a second peer connection / audio
  // element (which would play duplicated, overlapping voices).
  const connectEpochRef = useRef(0);

  const cleanupConnection = useCallback(() => {
    dcRef.current?.close(); dcRef.current = null;
    pcRef.current?.close(); pcRef.current = null;
    wsBackendRef.current?.close(); wsBackendRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current.srcObject = null; }
  }, []);

  const connect = useCallback(async () => {
    cleanupConnection();
    const epoch = ++connectEpochRef.current;
    const isStale = () => epoch !== connectEpochRef.current;
    setError(null);
    setStatusBoth('initializing');

    try {
      const tokenData = await APIService.getRealtimeToken(sid);
      if (isStale()) return;
      const qs = tokenData.questions || [];
      setTotalQ(tokenData.total_questions || 3);
      setQuestionsData(qs);
      questionsDataRef.current = qs;

      // Backend WebSocket — browsers can't set Authorization on a WS handshake,
      // so the Firebase ID token rides along as a query param the backend verifies.
      const wsToken = await getIdToken();
      const wsUrl = `${BACKEND_WS_URL}/ws/${sid}`
        + (wsToken ? `?token=${encodeURIComponent(wsToken)}` : '');
      const wsBackend = new WebSocket(wsUrl);
      wsBackendRef.current = wsBackend;

      wsBackend.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === 'tool_response') {
          if (msg.ui_update) applyUiUpdate(msg.ui_update);
          // Update the matching tool badge with the result
          setMessages(prev => {
            const idx = [...prev].reverse().findIndex(
              m => m.role === 'tool' && m.text?.call_id === msg.call_id
            );
            if (idx === -1) return prev;
            const realIdx = prev.length - 1 - idx;
            const updated = [...prev];
            updated[realIdx] = {
              ...updated[realIdx],
              text: { ...updated[realIdx].text, status: 'done', result: msg.result },
            };
            messagesRef.current = updated;
            return updated;
          });
          dcSend({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: msg.call_id, output: msg.result },
          });
          dcSend({ type: 'response.create' });
        }
      };
      wsBackend.onerror = () => console.warn('Backend WS error');

      // Microphone — MUTED until Begin (except on a mid-interview reconnect),
      // so server VAD can't start the interview off background noise.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      if (isStale()) { stream.getTracks().forEach(t => t.stop()); return; }
      stream.getAudioTracks().forEach((t) => { t.enabled = startedRef.current; });
      streamRef.current = stream;
      startNoiseWatch(stream);

      // RTCPeerConnection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audioEl = new Audio();
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      pc.ontrack = (e) => {
        if (e.track.kind === 'audio') audioEl.srcObject = e.streams[0];
      };
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = handleDcEvent;
      dc.onopen = () => {
        if (isStale()) return;
        dcSend({ type: 'session.update', session: tokenData.session_config });
        setStatusBoth('connected');
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (isStale()) return;

      const sdpResp = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST', body: offer.sdp,
          headers: { Authorization: `Bearer ${tokenData.ephemeral_key}`, 'Content-Type': 'application/sdp' },
        }
      );
      if (isStale()) return;
      if (!sdpResp.ok) throw new Error(`OpenAI WebRTC ${sdpResp.status}: ${await sdpResp.text()}`);
      await pc.setRemoteDescription({ type: 'answer', sdp: await sdpResp.text() });

    } catch (err) {
      console.warn('Voice init failed:', err);
      if (!isStale()) { setError(friendlyVoiceError(err)); setStatusBoth('error'); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  useEffect(() => {
    if (!sid) return undefined;          // still preparing — nothing to connect to yet
    connect();
    return () => {
      connectEpochRef.current++;   // invalidate any in-flight connect()
      stopNoiseWatch();
      cleanupConnection();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  useEffect(() => {
    if (dcRef.current) dcRef.current.onmessage = handleDcEvent;
  }, [handleDcEvent]);

  // ── Notification auto-dismiss ─────────────────────────────────────────────
  useEffect(() => {
    if (notifications.length === 0) return;
    const t = setTimeout(() => setNotifications(prev => prev.slice(1)), 4000);
    return () => clearTimeout(t);
  }, [notifications]);



  // ── Call-room view state (presentation only) ─────────────────────────────
  const [persona, setPersona] = useState(setup?.persona || 'professional');
  const [panel, setPanel] = useState(null);              // null | 'avatar' | 'settings'
  // The conversation is always available: a side panel on wide screens (it can
  // collapse to a slim rail), a drawer on narrow ones — captions show on the
  // stage whenever the panel isn't open.
  const wide = () => typeof window !== 'undefined' && window.innerWidth >= 1100;
  const [showTranscript, setShowTranscript] = useState(wide);
  const [showCaptions, setShowCaptions] = useState(true);
  const [showQuestion, setShowQuestion] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [copied, setCopied] = useState(false);
  const logRef = useRef(null);
  const atBottomRef = useRef(true);
  const [behind, setBehind] = useState(false);
  const onLogScroll = () => {
    const el = logRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = atBottom;
    if (atBottom) setBehind(false);
  };
  const jumpToLatest = () => {
    const el = logRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    atBottomRef.current = true; setBehind(false);
  };
  // Follow the newest line — unless the learner scrolled up to review.
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    else setBehind(true);
  }, [messages, aiPartial, userPartial, isUserSpeaking, userPending, showTranscript]);

  const aiActive  = status === 'speaking';
  const usrActive = isUserSpeaking;
  const preparing = !sid;
  const live = interviewStarted && status !== 'completed' && status !== 'error';
  const roleTitle = roleOf(setup?.roleId)?.title || sessionData?.title || '';
  const spoken = messages.filter((m) => m.role === 'ai' || m.role === 'user');
  const lastAi = [...spoken].reverse().find((m) => m.role === 'ai');
  const stateLabel = preparing ? 'Preparing'
    : status === 'error' ? 'Disconnected'
      : status === 'completed' ? 'Interview ended'
        : !interviewStarted ? (status === 'initializing' ? 'Connecting' : 'Ready')
          : 'Recording';
  const turn = !live ? null
    : aiActive ? { who: 'ai', text: 'AI is speaking' }
      : usrActive ? { who: 'you', text: 'You’re speaking' }
        : status === 'processing' || userPending ? { who: 'ai', text: 'Thinking…' }
          : { who: 'you', text: 'Your turn' };
  const leave = () => onCancel?.();
  const copyTranscript = () => {
    const txt = spoken.map((m) => `${m.role === 'ai' ? 'AI Interviewer' : 'You'} (${formatTime(m.t || 0)}): ${m.text}`).join('\n\n');
    try { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no clipboard */ }
  };

  const ctl = (key, label, icon, onClick, { on = false, off = false, disabled = false, title, badge } = {}) => (
    <button key={key} type="button" className={`pc-ctl${on ? ' on' : ''}${off ? ' off' : ''}`} onClick={onClick} disabled={disabled}
      aria-pressed={on || off || undefined} title={title || label}>
      <span className="pc-ctl-icon">{icon}{badge != null && <i className="pc-badge">{badge}</i>}</span>
      <span className="pc-ctl-label">{label}</span>
    </button>
  );

  return (
    <div className={`pu-call${showTranscript ? ' with-side' : ' side-closed'}`}>
      {notifications.length > 0 && (
        <div className={`pc-toast pc-toast-${notifications[0].msg_type}`}>{notifications[0].content}</div>
      )}

      <header className="pc-top">
        <span className="pc-mark" aria-hidden="true">P</span>
        <span className="pc-title">AI Interview{roleTitle ? <> · {roleTitle}</> : null}{setup?.company ? <em> ({setup.company})</em> : null}</span>
        <span className={`pc-live${live ? ' on' : ''}`}><i aria-hidden="true" />{stateLabel}</span>
        {interviewStarted && <span className="pc-time" aria-label="Elapsed time">{formatTime(elapsed)}</span>}
        {interviewStarted && status !== 'completed' && (
          <span className="pc-progress" aria-label={`Question ${Math.max(currentStage, 0)} of ${totalQ}`}>
            {currentStage === 0 ? 'Introduction' : `Question ${currentStage} of ${totalQ}`}
            <span className="pc-progress-bar"><i style={{ width: `${progress}%` }} /></span>
          </span>
        )}
        {live && <button type="button" className="pc-end pc-end-top" onClick={() => setConfirmEnd(true)}>End Interview</button>}
      </header>

      <div className="pc-body">
        <main className="pc-main">
          <div className={`pc-stage${aiActive ? ' speaking' : ''}${!interviewStarted || status === 'error' || status === 'completed' ? ' lobby' : ''}`}>
            <div className="pc-backdrop" aria-hidden="true" />
            <InterviewAvatar persona={persona} speaking={aiActive} label={`Alex, your ${personaOf(persona).label.toLowerCase()} interviewer`} />

            <div className="pc-nametag">
              <b>AI Interviewer</b><span>Alex · {personaOf(persona).label}</span>
              {live && <span className={`pc-ai-state${aiActive ? ' speaking' : ''}`}>{aiActive && <WaveBar active color="currentColor" count={4} />}{aiActive ? 'Speaking' : 'Listening'}</span>}
            </div>

            {showQuestion && live && currentStage > 0 && currentQData?.question && (
              <div className="pc-qcard"><span>Question {currentStage}{currentQData.topic ? ` · ${currentQData.topic}` : ''}</span><p>{currentQData.question}</p></div>
            )}

            {/* captions on the stage whenever the conversation panel is closed */}
            {showCaptions && live && !showTranscript && (aiPartial || lastAi || usrActive || userPartial) && (
              <div className="pc-caption" aria-hidden="true">
                {usrActive || userPartial ? (
                  <><span className="pc-cap-who">You</span>{userPartial ? <p>{userPartial}<span className="pc-cursor" /></p> : <span className="pc-typing"><span /><span /><span /></span>}</>
                ) : (
                  <><span className="pc-cap-who">AI</span><p>{aiPartial || lastAi?.text}{aiPartial && <span className="pc-cursor" />}</p></>
                )}
              </div>
            )}

            {noisy && live && <div className="pc-noise" role="status">We can hear background noise — a quieter spot will help the interviewer hear you.</div>}

            {!interviewStarted && status !== 'error' && status !== 'completed' && (
              <div className="pc-lobby">
                <h2>{preparing ? 'Preparing your interview' : status === 'initializing' ? 'Connecting the room…' : 'Your interviewer is ready'}</h2>
                <p>{preparing
                  ? `Picking your questions${roleTitle ? ` for ${roleTitle}` : ''} from your role and learning memory.`
                  : status === 'initializing' ? 'Your browser may ask for the microphone — allow it to talk to your interviewer.'
                    : 'Answer out loud, like a real interview. The conversation appears in the transcript as you talk.'}</p>
                <div className="pc-lobby-acts">
                  <button type="button" className="pc-begin" disabled={preparing || status === 'initializing'} onClick={handleStart}>
                    {preparing || status === 'initializing' ? <span className="pc-spin" aria-hidden="true" /> : null}Begin interview
                  </button>
                  <button type="button" className="pc-ghost" onClick={leave}>Leave</button>
                </div>
              </div>
            )}
            {interviewStarted && !aiJoined && status !== 'error' && status !== 'completed' && (
              <div className="pc-lobby small"><span className="pc-spin" aria-hidden="true" /> Your interviewer is joining — say hello when you hear the voice.</div>
            )}
            {status === 'error' && (
              <div className="pc-lobby">
                <span className={`pc-err-icon${error?.kind === 'mic' ? ' mic' : ''}`}>{error?.kind === 'mic' ? <IcMic size={26} /> : '!'}</span>
                <h2>{error?.title || 'Voice connection unavailable'}</h2>
                <p>{error?.body || 'Something interrupted the connection — give it another try.'}</p>
                {error?.steps && <ol className="pc-err-steps">{error.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>}
                <div className="pc-lobby-acts">
                  <button type="button" className="pc-begin" onClick={connect}>Try again</button>
                  <button type="button" className="pc-ghost" onClick={leave}>Leave</button>
                </div>
              </div>
            )}
            {status === 'completed' && (
              <div className="pc-lobby">
                <span className="pc-spin big" aria-hidden="true" />
                <h2>Scoring your interview</h2>
                <p>Reading your answers and writing your feedback — usually under a minute.</p>
              </div>
            )}

            <div className={`pc-self${usrActive ? ' speaking' : ''}`}>
              {camError || camOff ? (
                <div className="pc-self-off"><span>{camOff ? <IcCam off size={20} /> : <IcMic size={20} />}</span><small>{camOff ? 'Camera off' : 'No camera'}</small></div>
              ) : <video ref={videoRef} autoPlay playsInline muted />}
              <span className="pc-self-tag">You{micMuted ? <IcMic off size={12} /> : null}</span>
            </div>
          </div>

          {revealedHintsForQ.length > 0 && live && (
            <div className="pc-lifelines">
              {revealedHintsForQ.map((text, i) => (
                <div key={i} className="pc-lifeline"><span>Lifeline {i + 1} · {i === 0 ? 'free' : `−${i * 5} pts`}</span>{text}</div>
              ))}
            </div>
          )}

          <div className="pc-controls" role="toolbar" aria-label="Call controls">
            <div className="pc-ctl-group">
              {ctl('mic', micMuted ? 'Unmute' : 'Mute', <IcMic off={micMuted} />, toggleMic, { off: micMuted, title: micMuted ? 'Unmute microphone' : 'Mute microphone' })}
              {ctl('cam', camOff ? 'Start video' : 'Stop video', <IcCam off={camOff || camError} />, toggleCam, { off: camOff, disabled: camError && !camOff, title: camError ? 'No camera available' : undefined })}
              {ctl('hint', 'Lifeline', <IcBulb />, handleHint, {
                disabled: currentStage < 1 || hintCount === 0 || hintsUsed >= hintCount,
                badge: currentStage >= 1 ? Math.max(0, hintCount - hintsUsed) : undefined,
                title: currentStage < 1 ? 'Lifelines unlock once the first question is asked'
                  : hintsUsed >= hintCount ? 'No lifelines left for this question'
                    : hintsUsed === 0 ? 'First lifeline is free' : `Costs ${hintsUsed * 5} points on this question`,
              })}
              {ctl('settings', 'Settings', <IconGear size={18} />, () => setPanel(panel === 'settings' ? null : 'settings'), { on: panel === 'settings' })}
              {ctl('avatar', 'Change avatar', <IconPerson size={18} />, () => setPanel(panel === 'avatar' ? null : 'avatar'), { on: panel === 'avatar' })}
              {ctl('chat', showTranscript ? 'Hide transcript' : 'Transcript', <IconChat size={18} />, () => setShowTranscript((v) => !v), { on: showTranscript })}
            </div>
            {interviewStarted && status !== 'completed'
              ? <button type="button" className="pc-end" onClick={() => setConfirmEnd(true)}>End</button>
              : status !== 'completed' && <button type="button" className="pc-end ghost" onClick={leave}>Leave</button>}

            {panel === 'avatar' && (
              <div className="pc-pop pc-pop-avatar" role="dialog" aria-label="Change avatar">
                <div className="pc-pop-head"><b>Choose your interviewer</b><button type="button" onClick={() => setPanel(null)} aria-label="Close"><IconX size={15} /></button></div>
                <div className="pc-personas">
                  {PERSONAS.map((p) => (
                    <button key={p.id} type="button" className={persona === p.id ? 'on' : ''} aria-pressed={persona === p.id} onClick={() => setPersona(p.id)}>
                      <InterviewAvatar persona={p.id} size="sm" label={p.label} /><b>{p.label}</b><small>{p.note}</small>
                    </button>
                  ))}
                </div>
                <p>Changes how the interviewer looks. The voice stays the same.</p>
              </div>
            )}
            {panel === 'settings' && (
              <div className="pc-pop pc-pop-settings" role="dialog" aria-label="Interview settings">
                <div className="pc-pop-head"><b>Settings</b><button type="button" onClick={() => setPanel(null)} aria-label="Close"><IconX size={15} /></button></div>
                <label><input type="checkbox" checked={showCaptions} onChange={(e) => setShowCaptions(e.target.checked)} /> Captions on the video when the transcript is closed</label>
                <label><input type="checkbox" checked={showQuestion} onChange={(e) => setShowQuestion(e.target.checked)} /> Pin the current question on the video</label>
                <p>Microphone: {micMuted ? 'muted' : streamRef.current ? 'on' : 'waiting for permission'} · Camera: {camError ? 'unavailable' : camOff ? 'off' : 'on'}</p>
              </div>
            )}
          </div>
        </main>

        <aside className={`pc-side${showTranscript ? ' open' : ''}`} aria-label="Conversation">
          <div className="pc-side-head">
            <b>Conversation</b>
            {turn && <span className={`pc-turn ${turn.who}`}><i aria-hidden="true" />{turn.text}</span>}
            <span className="pc-side-acts">
              {spoken.length > 0 && <button type="button" onClick={copyTranscript}>{copied ? 'Copied' : 'Copy'}</button>}
              <button type="button" onClick={() => setShowTranscript(false)} aria-label="Collapse transcript"><IconX size={15} /></button>
            </span>
          </div>
          <div className="pc-transcript" ref={logRef} onScroll={onLogScroll} role="log" aria-live="polite" aria-relevant="additions">
            {spoken.length === 0 && !aiPartial && <p className="pc-empty">The conversation appears here as you talk — the interviewer’s questions and your answers, live.</p>}
            {spoken.map((m) => (
              <div key={m.id} className={`pc-msg ${m.role}`}>
                <div className="pc-msg-head"><b>{m.role === 'ai' ? 'AI Interviewer' : 'You'}</b><time>{formatTime(m.t || 0)}</time></div>
                <p>{m.text}</p>
              </div>
            ))}
            {aiPartial && (
              <div className="pc-msg ai live">
                <div className="pc-msg-head"><b>AI Interviewer</b><span className="pc-live-tag">speaking</span></div>
                <p>{aiPartial}<span className="pc-cursor" /></p>
              </div>
            )}
            {(usrActive || userPartial || userPending) && (
              <div className="pc-msg user live">
                <div className="pc-msg-head"><b>You</b><span className="pc-live-tag">{usrActive ? 'speaking' : 'transcribing'}</span></div>
                {userPartial ? <p>{userPartial}<span className="pc-cursor" /></p> : <p className="pc-typing" aria-label={usrActive ? 'You are speaking' : 'Transcribing your answer'}><span /><span /><span /></p>}
              </div>
            )}
          </div>
          {behind && <button type="button" className="pc-jump" onClick={jumpToLatest}>Jump to latest ↓</button>}
        </aside>
        {!showTranscript && (
          <button type="button" className="pc-side-rail" onClick={() => setShowTranscript(true)} aria-label="Open transcript">
            <IconChat size={16} /><span>Transcript</span>{spoken.length > 0 && <em>{spoken.length}</em>}
          </button>
        )}
      </div>

      {confirmEnd && (
        <div className="pc-confirm-veil" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmEnd(false); }}>
          <div className="pc-confirm" role="alertdialog" aria-modal="true" aria-labelledby="pc-confirm-h" aria-describedby="pc-confirm-p">
            <h2 id="pc-confirm-h">End this interview?</h2>
            <p id="pc-confirm-p">You’ll get feedback on the questions you’ve answered so far.</p>
            <div className="pc-confirm-acts">
              <button type="button" className="pc-ghost" autoFocus onClick={() => setConfirmEnd(false)}>Continue interview</button>
              <button type="button" className="pc-end" onClick={() => { setConfirmEnd(false); triggerCompletion(true); }}>End interview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInterviewInterface;
